import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewEncapsulation, computed, effect, input, output, signal, viewChild } from '@angular/core';

import type { MentionItem, MentionType } from '../../agent.types';

interface MentionGroup {
  type: MentionType;
  label: string;
  items: MentionItem[];
}

@Component({
  selector: 'agent-mention-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[style.top.px]': '$position().top',
    '[style.left.px]': '$position().left',
    '[class.visible]': '$isVisible()',
  },
  templateUrl: './mention.dropdown.component.html',
  styleUrls: ['./mention.dropdown.component.less'],
})
export class AgentMentionDropdownComponent implements OnDestroy {

  public $query = input<string>('', { alias: 'query' });
  public $results = input<MentionItem[]>([], { alias: 'results' });
  public $isVisible = input<boolean>(false, { alias: 'isVisible' });
  public $position = input<{ top: number; left: number }>({ top: 0, left: 0 }, { alias: 'position' });

  public selected = output<MentionItem>();
  public dismissed = output<void>();

  public $activeIndex = signal<number>(0);

  public $scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  public $groups = computed<MentionGroup[]>(() => {

    const results = this.$results();
    const groupOrder: MentionType[] = ['job', 'customer', 'contact'];
    const groupLabels: Record<MentionType, string> = { job: 'Jobs', customer: 'Customers', contact: 'Contacts' };

    return groupOrder
      .map(type => ({ type, label: groupLabels[type], items: results.filter(r => r.type === type) }))
      .filter(g => g.items.length > 0);
  });

  public $flatItems = computed<MentionItem[]>(() => {

    return this.$groups().flatMap(g => g.items);
  });

  public $hasResults = computed<boolean>(() => this.$flatItems().length > 0);

  private boundOnKeydown = this.onKeydown.bind(this);
  private boundOnClickOutside = this.onClickOutside.bind(this);

  constructor() {

    effect(() => {

      if (this.$isVisible()) {
        this.$activeIndex.set(0);
        document.addEventListener('keydown', this.boundOnKeydown);
        document.addEventListener('mousedown', this.boundOnClickOutside);
      } else {
        document.removeEventListener('keydown', this.boundOnKeydown);
        document.removeEventListener('mousedown', this.boundOnClickOutside);
      }
    });

    effect(() => {

      this.$results();
      this.$activeIndex.set(0);
    });
  }

  public ngOnDestroy(): void {

    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('mousedown', this.boundOnClickOutside);
  }

  public onItemClick(item: MentionItem): void {

    this.selected.emit(item);
  }

  public onItemHover(flatIndex: number): void {

    this.$activeIndex.set(flatIndex);
  }

  public isActiveItem(item: MentionItem): boolean {

    const flatItems = this.$flatItems();
    const index = flatItems.indexOf(item);
    return index === this.$activeIndex();
  }

  public getInitials(label: string): string {

    return label
      .split(' ')
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  }

  public getFlatIndex(group: MentionGroup, itemIndex: number): number {

    const groups = this.$groups();
    let offset = 0;
    for (const g of groups) {
      if (g === group) break;
      offset += g.items.length;
    }
    return offset + itemIndex;
  }

  private onKeydown(event: KeyboardEvent): void {

    if (!this.$isVisible()) return;

    const flatItems = this.$flatItems();
    if (flatItems.length === 0 && event.key !== 'Escape') return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.$activeIndex.update(i => (i + 1) % flatItems.length);
        this.scrollActiveIntoView();
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.$activeIndex.update(i => (i - 1 + flatItems.length) % flatItems.length);
        this.scrollActiveIntoView();
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const activeItem = flatItems[this.$activeIndex()];
        if (activeItem) {
          this.selected.emit(activeItem);
        }
        break;
      }
      case 'Escape': {
        event.preventDefault();
        this.dismissed.emit();
        break;
      }
    }
  }

  private onClickOutside(event: MouseEvent): void {

    const container = this.$scrollContainer()?.nativeElement?.closest('agent-mention-dropdown');
    if (container && !container.contains(event.target as Node)) {
      this.dismissed.emit();
    }
  }

  private scrollActiveIntoView(): void {

    const container = this.$scrollContainer()?.nativeElement;
    if (!container) return;

    const activeEl = container.querySelector('.item.active') as HTMLElement;
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }
}
